import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Subject } from 'rxjs';
import { ResponseModel } from '@shared/models/response.model';
import { StaffModel } from '@shared/models/staff.model';
import { getCurrentContextIds } from '@shared/utils/current-context.util';

type GetPagedDataParams = {
  page?: number;
  perPage?: number;
  sort?: string | null;
  filterKeyword?: string | null;
  filterStatus?: string | null;
  role?: string | null;
};

@Injectable({ providedIn: 'root' })
export class StaffService {
  private update = new Subject<string>();
  constructor(private http: HttpClient) {}

  getAll({
    page = 0,
    perPage = 5,
    sort = null,
    filterKeyword = null,
    role = null,
  }: GetPagedDataParams = {}) {
    let params = new HttpParams().set('page', page).set('per_page', perPage);
    if (sort !== null) {
      params = params.set('sort', sort);
    }
    if (role !== null) {
      params = params.set('filter[role]', role);
    }
    if (filterKeyword !== null) {
      params = params.set('filter[search]', filterKeyword);
    }
    const { restaurantId, branchId } = getCurrentContextIds();
    return this.http.get<ResponseModel<StaffModel[]>>(`restaurants/${restaurantId}/branches/${branchId}/staff`, {
      params,
    });
  }

  addMemberToStaff(member: object) {
    const { restaurantId, branchId } = getCurrentContextIds();
    return this.http.post<ResponseModel<StaffModel>>(
      `restaurants/${restaurantId}/branches/${branchId}/staff`,
      member
    );
  }

  getMemberOfStaffByID(id: string) {
    const { restaurantId, branchId } = getCurrentContextIds();
    return this.http.get<ResponseModel<StaffModel>>(
      `restaurants/${restaurantId}/branches/${branchId}/staff/${id}`
    );
  }

  editMemberInStaff(id: string, member: object) {
    const { restaurantId, branchId } = getCurrentContextIds();
    return this.http.post<ResponseModel<StaffModel>>(
      `restaurants/${restaurantId}/branches/${branchId}/staff/${id}?_method=PUT`,
      member
    );
  }

  assignStaffToBranches(id: string, branchIds: string[], sourceBranchId?: string) {
    const { restaurantId, branchId } = getCurrentContextIds();
    const sourceId = sourceBranchId || branchId;
    const body: { branch_ids: string[] } = {
      branch_ids: branchIds,
    };

    return this.http.put<ResponseModel<StaffModel>>(
      `restaurants/${restaurantId}/branches/${sourceId}/staff/${id}/assign-branch`,
      body
    );
  }

  deleteMemberFromStaff(id: string) {
    const { restaurantId, branchId } = getCurrentContextIds();
    return this.http.delete(`restaurants/${restaurantId}/branches/${branchId}/staff/${id}`);
  }
  blockStaff(id: string) {
    const { restaurantId, branchId } = getCurrentContextIds();
    return this.http.post<ResponseModel<StaffModel>>(
      `restaurants/${restaurantId}/branches/${branchId}/staff/${id}/block?_method=PUT`, null
    );
  }

  unBlockStaff(id: string) {
    const { restaurantId, branchId } = getCurrentContextIds();
    return this.http.post<ResponseModel<StaffModel>>(
      `restaurants/${restaurantId}/branches/${branchId}/staff/${id}/unblock?_method=PUT`, null
    );
  }

  resetPassword(id: string, new_password) {
    const { restaurantId, branchId } = getCurrentContextIds();
    return this.http.post<ResponseModel<StaffModel>>(
      `restaurants/${restaurantId}/branches/${branchId}/staff/${id}/reset-password?_method=PUT`, new_password
    );
  }

  sendUpdate(message: string) {
    this.update.next(message);
  }

  getUpdate() {
    return this.update.asObservable();
  }
}
